import { useState } from 'react';
import { Shield, Plus, Settings, Bell, ChevronRight, CheckCircle, XCircle, AlertTriangle, Clock } from 'lucide-react';

interface Pipeline {
  id: string;
  name: string;
  description: string;
  ciPlatform: string;
  plan: string;
  createdAt: string;
  lastRun?: {
    passed: boolean;
    passedTests: number;
    totalTests: number;
    createdAt: string;
  };
}

// Mock data for demo
const mockPipelines: Pipeline[] = [
  {
    id: 'pipe_demo1',
    name: 'support-agent-v2',
    description: 'Customer support agent production pipeline',
    ciPlatform: 'github_actions',
    plan: 'pro',
    createdAt: '2024-06-01T10:00:00Z',
    lastRun: { passed: true, passedTests: 4, totalTests: 4, createdAt: '2024-06-09T15:30:00Z' },
  },
  {
    id: 'pipe_demo2',
    name: 'sales-bot-main',
    description: 'Sales qualification bot',
    ciPlatform: 'gitlab_ci',
    plan: 'starter',
    createdAt: '2024-06-03T14:00:00Z',
    lastRun: { passed: false, passedTests: 3, totalTests: 4, createdAt: '2024-06-09T12:15:00Z' },
  },
  {
    id: 'pipe_demo3',
    name: 'docs-assistant',
    description: 'Documentation generation assistant',
    ciPlatform: 'github_actions',
    plan: 'starter',
    createdAt: '2024-06-05T09:00:00Z',
    lastRun: { passed: true, passedTests: 2, totalTests: 2, createdAt: '2024-06-08T18:45:00Z' },
  },
];

export default function Dashboard() {
  const [pipelines] = useState<Pipeline[]>(mockPipelines);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const stats = {
    totalPipelines: pipelines.length,
    testRunsThisMonth: 127,
    regressionsCaught: 8,
    avgPassRate: 94,
  };

  const recentActivity = [
    { id: 1, pipeline: 'support-agent-v2', status: 'passed', time: '2 hours ago' },
    { id: 2, pipeline: 'sales-bot-main', status: 'failed', time: '4 hours ago' },
    { id: 3, pipeline: 'docs-assistant', status: 'passed', time: '1 day ago' },
    { id: 4, pipeline: 'support-agent-v2', status: 'passed', time: '2 days ago' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-accent" />
                <span className="font-mono font-semibold text-text-primary">Regression Guard</span>
              </a>
              <span className="px-2 py-1 bg-accent/10 border border-accent/20 rounded text-accent text-xs font-medium">
                Pro
              </span>
            </div>

            <div className="flex items-center gap-4">
              <button className="p-2 text-text-secondary hover:text-text-primary transition-colors">
                <Bell className="w-5 h-5" />
              </button>
              <button className="p-2 text-text-secondary hover:text-text-primary transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <div className="w-8 h-8 bg-accent/20 border border-accent/30 rounded-full flex items-center justify-center text-accent font-medium text-sm">
                A
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl font-mono font-bold text-text-primary">Dashboard</h1>
            <p className="text-text-secondary text-sm mt-1">Monitor your AI pipeline health</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Pipeline
          </button>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="text-text-secondary text-xs mb-1">Total Pipelines</div>
            <div className="text-2xl font-mono font-bold text-text-primary">{stats.totalPipelines}</div>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="text-text-secondary text-xs mb-1">Test Runs (Month)</div>
            <div className="text-2xl font-mono font-bold text-text-primary">{stats.testRunsThisMonth}</div>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="text-text-secondary text-xs mb-1">Regressions Caught</div>
            <div className="text-2xl font-mono font-bold text-fail">{stats.regressionsCaught}</div>
          </div>
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="text-text-secondary text-xs mb-1">Avg Pass Rate</div>
            <div className="text-2xl font-mono font-bold text-pass">{stats.avgPassRate}%</div>
          </div>
        </div>

        {/* Pipelines grid */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Pipelines</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pipelines.map((pipeline) => (
              <a
                key={pipeline.id}
                href={`/pipeline/${pipeline.id}`}
                className="bg-surface border border-border rounded-xl p-5 card-hover group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-text-primary group-hover:text-accent transition-colors">
                      {pipeline.name}
                    </h3>
                    <p className="text-text-secondary text-xs mt-1">{pipeline.description}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-text-secondary group-hover:text-accent transition-colors" />
                </div>

                <div className="flex items-center gap-4 text-xs">
                  {pipeline.lastRun ? (
                    <>
                      <div className={`flex items-center gap-1 ${pipeline.lastRun.passed ? 'text-pass' : 'text-fail'}`}>
                        {pipeline.lastRun.passed ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        {pipeline.lastRun.passedTests}/{pipeline.lastRun.totalTests} passed
                      </div>
                      <div className="flex items-center gap-1 text-text-secondary">
                        <Clock className="w-3 h-3" />
                        {new Date(pipeline.lastRun.createdAt).toLocaleDateString()}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-1 text-text-secondary">
                      <AlertTriangle className="w-3 h-3" />
                      No runs yet
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                  <span className="text-xs px-2 py-0.5 bg-background rounded text-text-secondary capitalize">
                    {pipeline.ciPlatform.replace('_', ' ')}
                  </span>
                  <span className="text-xs px-2 py-0.5 bg-accent/10 border border-accent/20 rounded text-accent capitalize">
                    {pipeline.plan}
                  </span>
                </div>
              </a>
            ))}

            {/* Add new pipeline card */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-surface/50 border border-dashed border-border rounded-xl p-5 flex flex-col items-center justify-center gap-2 text-text-secondary hover:text-text-primary hover:border-text-secondary transition-colors"
            >
              <Plus className="w-6 h-6" />
              <span className="text-sm">Add Pipeline</span>
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h2 className="text-lg font-semibold text-text-primary mb-4">Recent Activity</h2>
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            {recentActivity.map((activity, i) => (
              <div
                key={activity.id}
                className={`flex items-center justify-between px-4 py-3 ${i !== recentActivity.length - 1 ? 'border-b border-border' : ''}`}
              >
                <div className="flex items-center gap-3">
                  {activity.status === 'passed' ? (
                    <CheckCircle className="w-4 h-4 text-pass" />
                  ) : (
                    <XCircle className="w-4 h-4 text-fail" />
                  )}
                  <span className="text-text-primary text-sm">{activity.pipeline}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-xs px-2 py-0.5 rounded ${activity.status === 'passed' ? 'bg-pass/10 text-pass' : 'bg-fail/10 text-fail'}`}>
                    {activity.status}
                  </span>
                  <span className="text-text-secondary text-xs">{activity.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Create Pipeline Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-surface border border-border rounded-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Create New Pipeline</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-text-secondary text-sm mb-1">Pipeline Name</label>
                <input
                  type="text"
                  placeholder="e.g., support-agent-v2"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-secondary/50 focus:border-accent focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-1">Description</label>
                <textarea
                  placeholder="Brief description of what this pipeline tests"
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-secondary/50 focus:border-accent focus:outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-1">CI Platform</label>
                <select className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary focus:border-accent focus:outline-none">
                  <option value="github_actions">GitHub Actions</option>
                  <option value="gitlab_ci">GitLab CI</option>
                  <option value="webhook">Webhook Only</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 border border-border hover:border-text-secondary text-text-primary text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button className="flex-1 px-4 py-2 bg-accent hover:bg-accent/90 text-white text-sm font-medium rounded-lg transition-colors">
                Create Pipeline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}